import { computed, ref } from 'vue';
import {
  transformSymbolTradingStateChange,
  transformTradePriceChange,
  transformClosingPriceChange,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  useSymbolDeductions,
  TransformTradePriceChangeDataType,
  TransformSymbolTradingStateChangeDataType,
  transformPreOpeningTradePriceChange,
  SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX,
  TransformPreOpeningTradePriceChangeDataType,
  transformPricePercentage,
} from '@core/symbol';
import { UseLiveQueryConfigV2, useLiveQueryV2, CODES } from '@libs/use';
import { PortfolioItem, PortfolioQuery } from '@libs/api-reference';
import { useUser } from '@core/user';

import * as API from '../requests';
import {
  PORTFOLIO_QUERY_KEYS,
  PORTFOLIO_RESULT_STATE,
  PORTFOLIO_UPDATE_CHANNEL_PREFIX,
} from '../constants';
import {
  PortfolioItemWithPrices,
  calcPortfolioReturnValuePercentage,
  calcPortfolioSymbolPrices,
} from '../utils/calculator';

type UsePortfolioQueryType = {
  listen?: boolean;
  onSuccess?: UseLiveQueryConfigV2<PortfolioQuery>['onSuccess'];
  unsubscribe?: UseLiveQueryConfigV2<PortfolioQuery>['unsubscribe'];
  listenToPreOpeningPriceChannel?: boolean;
};

export type PortfolioStats = {
  sumValues: number;
  sumReturnValues: number;
  sumReturnValuesPercentage: number;
  sumBuyTotalTradeVolumes: number;
};

export default function useLivePortfolioQuery(
  {
    listen = true,
    onSuccess,
    unsubscribe = true,
    listenToPreOpeningPriceChannel = false,
  }: UsePortfolioQueryType = {
    listen: true,
    listenToPreOpeningPriceChannel: false,
  }
) {
  const { findWageConfigBySymbol, deductions } = useSymbolDeductions();

  const { user, isUserReady } = useUser();

  const isQueryEnable = computed(
    () => Boolean(deductions.value.length) && isUserReady.value
  );

  const symbols = ref<PortfolioItemWithPrices[]>([]);
  const isSymbolLoaded = ref(false);

  const resultState = ref<number>(PORTFOLIO_RESULT_STATE.error);

  const {
    execute: getPortfolio,
    isPending: isLoadingSymbols,
    isRevalidating: isRevalidatingSymbols,
    isError: isErrorLoadingPortfolio,
    isSuccess: isSuccessLoadingPortfolio,
    status: portfolioStatus,
  } = useLiveQueryV2<PortfolioQuery>({
    key: PORTFOLIO_QUERY_KEYS.portfolio,
    fetcher: API.getPortfolio,
    unsubscribe,
    immediate: isQueryEnable,
    getChannels: listen
      ? (result) => {
          const symbols = result?.data.items || [];
          resultState.value =
            result?.data.resultState ?? PORTFOLIO_RESULT_STATE.error;

          return symbols?.reduce((symbolsChannels: string[], currentSymbol) => {
            if (listenToPreOpeningPriceChannel) {
              symbolsChannels.push(
                `${SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX}${currentSymbol.instrumentIsin}`
              );
            }

            symbolsChannels.push(
              `${SYMBOL_TRADE_CHANNEL_PREFIX}${currentSymbol.instrumentIsin}`,
              `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${currentSymbol.instrumentIsin}`,
              `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${currentSymbol.instrumentIsin}`,
              `${PORTFOLIO_UPDATE_CHANNEL_PREFIX}${user.value?.bimCode}`
            );

            return symbolsChannels;
          }, []);
        }
      : () => [],
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.SymbolTradePriceChangePreOpeningSignal]:
          transformPreOpeningTradePriceChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
      };

      const items = symbols.value;

      if (actions[signal.code] && items?.length) {
        const data = actions[signal.code](
          signal.data as TransformTradePriceChangeDataType &
            TransformSymbolTradingStateChangeDataType &
            TransformPreOpeningTradePriceChangeDataType
        );

        const index = items.findIndex((s) => s.instrumentIsin === data.isin);

        if (index > -1) {
          const item = items[index];

          if (
            signal.code === CODES.SymbolTradePriceChangeSignal &&
            item.lastTradePrice !== data.lastTradePrice
          ) {
            Object.assign(
              item,
              calcPortfolioSymbolPrices({
                ...item,
                ...data,
              })
            );
          } else if (signal.code !== CODES.SymbolTradePriceChangeSignal) {
            Object.assign(item, data);
          }
        }
      } else if (signal.code === CODES.PortfolioUpdateSignal) {
        if (!isRevalidatingSymbols.value) {
          getPortfolio();
        }
      }
    },
    onSuccess(response) {
      if (onSuccess) onSuccess(response);

      const items = response.data.items;

      if (items && items.length) {
        symbols.value = items.map(
          (symbol: PortfolioItem & { pricePercentage?: number }) => {
            transformPricePercentage(symbol);

            return calcPortfolioSymbolPrices({
              ...symbol,
              wage: findWageConfigBySymbol(symbol),
            });
          }
        );
      }

      isSymbolLoaded.value = true;
    },
  });

  const stats = computed<PortfolioStats>(() => {
    const defaultStats = {
      sumValues: 0,
      sumReturnValues: 0,
      sumReturnValuesPercentage: 0,
      sumBuyTotalTradeVolumes: 0,
    };

    if (!symbols.value?.length) {
      return defaultStats;
    }

    const { sumValues, sumReturnValues, sumBuyTotalTradeVolumes } =
      symbols.value.reduce(
        (total, symbol) => {
          total.sumBuyTotalTradeVolumes += symbol.buyTotalTradeVolume;
          total.sumReturnValues += symbol.returnValue;
          total.sumValues += symbol.value;

          return total;
        },
        { sumValues: 0, sumReturnValues: 0, sumBuyTotalTradeVolumes: 0 }
      );

    return {
      sumValues,
      sumReturnValues,
      sumReturnValuesPercentage: calcPortfolioReturnValuePercentage(
        sumReturnValues,
        sumBuyTotalTradeVolumes
      ),
      sumBuyTotalTradeVolumes,
    };
  });

  return {
    symbols,
    isLoadingSymbols,
    isRevalidatingSymbols,
    isSymbolLoaded,
    stats,
    isErrorLoadingPortfolio,
    isSuccessLoadingPortfolio,
    getPortfolio,
    portfolioStatus,
    isFetchingPortfolio: computed(
      () => isLoadingSymbols.value || isRevalidatingSymbols.value
    ),
    resultState,
  };
}
